import React, { useCallback } from "react";
import { FlaskConical } from "lucide-react";
import Layout from "src/components/app/layout/Layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "src/redux/hooks";
import { searchLabTestsRequest } from "src/redux/slices/labTestSlice";
import { SearchLabTestResult } from "src/types/labTests.types";
import GenericSearchScreen from "src/components/common/Search/GenericSearchScreen";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

interface Address {
  formatted_address: string;
  geometry: {
    location: { lat: number; lng: number };
  };
}

interface SearchLocationProps {
  show?: boolean;
  onClose?: () => void;
  onAddressSelect?: (address: Address) => void;
}

const SearchListView: React.FC<{
  item: any;
  index: number;
}> = ({ item, index }) => {
  const {
    place_id,
    structured_formatting: { main_text, secondary_text },
  } = item;
  return (
    <div key={index} className="flex items-start space-x-3">
      {/* Icon */}
      {/* <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
          <FlaskConical className="w-5 h-5 text-gray-700" />
        </div> */}

      {/* Text */}
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {main_text}
        </p>
        <p className="text-xs text-gray-500 truncate">{secondary_text}</p>
      </div>
    </div>
  );
};

const SearchLocation: React.FC<SearchLocationProps> = ({
  show,
  onClose,
  onAddressSelect,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    cache: 24 * 60 * 60,
  });

  const locationContext = useLocation();
  const isLocationToEdit = locationContext.state?.isLocationToEdit;
  const addressId = locationContext.state?.addressId;
  const addressType = locationContext.state?.addressType;
  const selectedLocation = locationContext.state?.selectedLocation;
  const formatted_address = locationContext.state?.formatted_address;
  console.log("locations state", locationContext.state);
  const handleSelect = async (suggestion: any) => {
    console.log("handle select and suggestion", suggestion);
    setValue(suggestion.description, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address: suggestion.description });
      const { lat, lng } = await getLatLng(results[0]);
      console.log("results", results);
      console.log("description", suggestion.description);
      console.log("lat", lat);
      console.log("lng", lng);
      // Navigate back to map screen with selected location and address
      navigate(`/app/${supportProgramKey}/book-lab-test/add-complete-address`, {
        state: {
          searchedLocation: { lat, lng },
          formatted_address: suggestion.description,
          ...(isLocationToEdit && {
            addressId,
            addressType,
            isLocationToEdit,
            selectedLocation,
            formatted_address,
          }),
        },
      });
      //   onAddressSelect({
      //     formatted_address: suggestion.description,
      //     geometry: {
      //       location: { lat, lng },
      //     },
      //   });
    } catch (error) {
      console.error("Error selecting address:", error);
    }
  };

  const fetchResults = async (searchTerm = "", page = 1) => {
    setValue(searchTerm, false); // Trigger autocomplete search
    // Wait a bit for suggestions to be updated
    await new Promise((res) => setTimeout(res, 300)); // wait for debounce

    if (status !== "OK") {
      return { items: [], hasMore: false };
    }

    // Transform the data into an array of SearchLabTestResult or your expected item type
    const results = data.map((suggestion) => ({
      description: suggestion.description,
      place_id: suggestion.place_id,
    }));

    return {
      items: results,
      hasMore: false, // Google Places Autocomplete does not support pagination
    };
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      {/* <GenericSearchScreen<any>
        title="Search for area, street name..."
        placeholder="Search for area, street name..."
        fetchResults={fetchResults}
        renderItem={(item, index) => (
          <SearchListView key={index} item={item} index={index} />
        )}
        inputDisabled={!ready}
        setValue={setValue}
      /> */}
      <GenericSearchScreen<any>
        title="Search for area, street name..."
        placeholder="Search for area, street name..."
        items={data}
        renderItem={(item, index) => (
          <div
            key={index}
            className="p-4 cursor-pointer hover:bg-gray-50"
            onClick={() => handleSelect(item)}
          >
            <SearchListView item={item} index={index} />
          </div>
        )}
        inputDisabled={!ready}
        onInputChange={setValue}
        searchValue={value}
        onBackClick={() => {
          navigate(
            `/app/${supportProgramKey}/book-lab-test/add-complete-address`,
            {
              state: {
                searchedLocation: selectedLocation,
                formatted_address: formatted_address,
                ...(isLocationToEdit && {
                  addressId,
                  addressType,
                  isLocationToEdit,
                  selectedLocation,
                  formatted_address,
                }),
              },
            }
          );
        }}
      />
    </Layout>
  );
};

export default SearchLocation;
